import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MetricasReservas from "@/pages/MetricasReservas";
import BuscarDisponibilidad from "@/components/reservas/BuscarDisponibilidad";

function fmtISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtFecha(d) {
    const s = d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function hhmm(h, m) { return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`; }
function toMin(s) { const [h, m] = s.split(":"); return parseInt(h) * 60 + parseInt(m); }
function fromMin(min) { return hhmm(Math.floor(min / 60), min % 60); }

export default function ReservasOperaciones() {
    const [salas, setSalas] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [tab, setTab] = useState("reservas");
    const [subMode, setSubMode] = useState("buscar"); // buscar | calendario

    // Calendario
    const [salaId, setSalaId] = useState("");
    const [day, setDay] = useState(() => { const x = new Date(); x.setHours(0, 0, 0, 0); return x; });
    const [unidadId, setUnidadId] = useState("");
    const [reservasDia, setReservasDia] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [busyCancel, setBusyCancel] = useState(null);
    const [now, setNow] = useState(() => new Date());

    // Refrescar la "hora actual" cada 60s para que los slots pasados se vayan deshabilitando
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(id);
    }, []);

    const today0 = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, [now]); // eslint-disable-line
    const isToday = day.getTime() === today0.getTime();
    const isPastDay = day.getTime() < today0.getTime();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    // Si por estado el día queda en el pasado, reset a hoy.
    useEffect(() => {
        if (isPastDay) setDay(today0);
    }, [isPastDay, today0]);

    useEffect(() => {
        api.get("/salas").then((r) => {
            const activas = r.data.filter((s) => s.activa);
            setSalas(activas);
            if (activas[0]) setSalaId(activas[0].sala_id);
        });
        api.get("/unidades").then((r) => setUnidades(r.data));
    }, []);

    const fecha = fmtISODate(day);
    const sala = useMemo(() => salas.find((s) => s.sala_id === salaId), [salas, salaId]);
    const dayIdx = (day.getDay() + 6) % 7;
    const disponibleEseDia = sala?.dias_disponibles?.includes(dayIdx);

    const loadReservasDia = useCallback(() => {
        if (!salaId) return Promise.resolve();
        return api.get("/reservas", { params: { sala_id: salaId, fecha_desde: fecha, fecha_hasta: fecha } })
            .then((r) => setReservasDia(r.data))
            .catch(() => setReservasDia([]));
    }, [salaId, fecha]);

    useEffect(() => { loadReservasDia(); }, [loadReservasDia]);
    useEffect(() => { setSelected(new Set()); }, [salaId, fecha]);

    // Limpiar selecciones que quedaron en el pasado al pasar el tiempo
    useEffect(() => {
        if (!isToday) return;
        setSelected((prev) => {
            const next = new Set(prev);
            let changed = false;
            for (const label of prev) {
                if (toMin(label) < nowMin) { next.delete(label); changed = true; }
            }
            return changed ? next : prev;
        });
    }, [nowMin, isToday]);

    const salaName = (id) => salas.find((s) => s.sala_id === id)?.nombre || "—";
    const uniName = (id) => unidades.find((u) => u.unidad_id === id)?.etiqueta || "—";

    const slots = useMemo(() => {
        if (!sala) return [];
        const arr = [];
        const ini = toMin(sala.horario_inicio);
        const fin = toMin(sala.horario_fin);
        for (let m = ini; m < fin; m += 30) {
            arr.push({ label: hhmm(Math.floor(m / 60), m % 60) });
        }
        return arr;
    }, [sala]);

    const reservaDe = useCallback((slot) => {
        const ini = toMin(slot.label);
        const fin = ini + 30;
        return reservasDia.find((r) => r.estado !== "cancelada"
            && toMin(r.hora_inicio) < fin && toMin(r.hora_fin) > ini);
    }, [reservasDia]);

    const toggleSlot = (slot) => {
        // Bloquear slots pasados del día actual
        if (isToday && toMin(slot.label) < nowMin) return;
        const r = reservaDe(slot);
        if (r) { cancelar(r); return; }
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(slot.label)) next.delete(slot.label);
            else next.add(slot.label);
            return next;
        });
    };

    const reservar = async () => {
        if (!unidadId) return toast.error("Elegí la unidad para la cual reservás");
        if (selected.size === 0) return toast.error("Seleccioná al menos un horario");
        if (submitting) return;
        setSubmitting(true);
        const labels = [...selected].sort();
        const results = await Promise.allSettled(
            labels.map((label) => {
                const ini = toMin(label);
                return api.post("/reservas", {
                    sala_id: salaId,
                    unidad_id: unidadId,
                    fecha,
                    hora_inicio: label,
                    hora_fin: fromMin(ini + 30),
                    asistentes: 1,
                    motivo: "",
                });
            })
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        await loadReservasDia();
        setSelected(new Set());
        if (fail === 0) {
            toast.success(ok === 1 ? "Reserva creada" : `${ok} bloques reservados`);
        } else {
            const firstErr = results.find((r) => r.status === "rejected");
            toast.error(firstErr ? formatApiError(firstErr.reason) : "Algunos horarios no estaban disponibles");
        }
        setSubmitting(false);
    };

    const cancelar = async (r) => {
        if (!window.confirm(`¿Cancelar reserva ${uniName(r.unidad_id)} · ${r.hora_inicio}–${r.hora_fin}?`)) return;
        setBusyCancel(r.reserva_id);
        try {
            await api.post(`/reservas/${r.reserva_id}/cancelar`);
            toast.success("Reserva cancelada");
            await loadReservasDia();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyCancel(null); }
    };

    const goToday = () => { const x = new Date(); x.setHours(0, 0, 0, 0); setDay(x); };
    const count = selected.size;

    return (
        <div>
            <PageHeader
                title="Reserva salas"
                subtitle="Gestioná reservas y analizá el uso de las salas."
            />
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="reservas" data-testid="tab-reservas">Reservas</TabsTrigger>
                    <TabsTrigger value="metricas" data-testid="tab-metricas">Métricas</TabsTrigger>
                </TabsList>

                {/* RESERVAS (Buscar disponibilidad | Calendario por sala) */}
                <TabsContent value="reservas" className="space-y-4">
                    <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                        <button
                            type="button"
                            onClick={() => setSubMode("buscar")}
                            data-testid="oper-mode-buscar"
                            className={`px-3 text-xs font-medium transition-colors ${subMode === "buscar" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                        >
                            Buscar disponibilidad
                        </button>
                        <button
                            type="button"
                            onClick={() => setSubMode("calendario")}
                            data-testid="oper-mode-calendario"
                            className={`px-3 text-xs font-medium transition-colors border-l border-border ${subMode === "calendario" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                        >
                            Calendario por sala
                        </button>
                    </div>

                    {subMode === "buscar" ? (
                        <BuscarDisponibilidad
                            unidades={unidades}
                            unidadId={unidadId}
                            onChangeUnidad={setUnidadId}
                            onReservaCreada={loadReservasDia}
                        />
                    ) : (
                      <>
                    <Card>
                        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Sala</Label>
                                <Select value={salaId} onValueChange={setSalaId}>
                                    <SelectTrigger data-testid="cal-sala"><SelectValue placeholder="Elegí sala" /></SelectTrigger>
                                    <SelectContent>
                                        {salas.map((s) => <SelectItem key={s.sala_id} value={s.sala_id}>{s.nombre}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Fecha</Label>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setDay(addDays(day, -1))}
                                        disabled={isToday}
                                        data-testid="cal-prev"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={goToday}
                                        className="flex-1 px-3 py-2 rounded-md border border-border bg-card hover:bg-primary-soft transition-colors text-sm font-heading font-bold text-center"
                                    >
                                        {fmtFecha(day)}
                                    </button>
                                    <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, 1))} data-testid="cal-next">
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Reservar para</Label>
                                <Select value={unidadId} onValueChange={setUnidadId}>
                                    <SelectTrigger data-testid="cal-unidad"><SelectValue placeholder="Elegí unidad" /></SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {unidades.map((u) => (
                                            <SelectItem key={u.unidad_id} value={u.unidad_id}>{u.etiqueta}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Leyenda compacta */}
                    <div className="flex items-center gap-x-3 text-[10px] text-muted-foreground flex-wrap">
                        <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-accent/40 ring-1 ring-accent/30" /> Disponible
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Seleccionado
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-success" /> Reservado · click para liberar
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/20" /> No disponible
                        </span>
                    </div>

                    {!sala ? (
                        <p className="text-center text-sm text-muted-foreground py-6">Elegí una sala para ver la grilla.</p>
                    ) : !disponibleEseDia ? (
                        <Card className="border-dashed">
                            <CardContent className="p-8 text-center text-sm text-muted-foreground">
                                La sala no está disponible este día.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 pb-28">
                            {slots.map((s) => {
                                const r = reservaDe(s);
                                const isSelected = selected.has(s.label);
                                const isCanceling = r && busyCancel === r.reserva_id;
                                const isPastSlot = isToday && toMin(s.label) < nowMin;
                                let cls;
                                if (isPastSlot && !r) {
                                    cls = "bg-muted/40 text-muted-foreground/50 border border-dashed border-border cursor-not-allowed line-through decoration-1";
                                } else if (r) {
                                    cls = "bg-success/90 text-white border-2 border-success hover:bg-success/70 active:scale-95 transition-all";
                                } else if (isSelected) {
                                    cls = "bg-primary text-primary-foreground border-2 border-primary shadow-md scale-[0.98] hover:bg-primary/90 transition-all";
                                } else {
                                    cls = "bg-accent/15 text-accent border border-accent/30 hover:bg-accent/30 active:scale-95 transition-all";
                                }
                                let testid;
                                if (isPastSlot && !r) testid = `slot-${s.label}-past`;
                                else if (r) testid = `slot-${s.label}-busy`;
                                else if (isSelected) testid = `slot-${s.label}-sel`;
                                else testid = `slot-${s.label}-free`;
                                return (
                                    <button
                                        type="button"
                                        key={s.label}
                                        disabled={isCanceling || submitting || (isPastSlot && !r)}
                                        onClick={() => toggleSlot(s)}
                                        data-testid={testid}
                                        title={r ? `${r.unidad_etiqueta || uniName(r.unidad_id)} · ${r.hora_inicio}–${r.hora_fin}${r.creado_por_nombre ? `\nCreada por ${r.creado_por_nombre}` : ""}${r.creado_por_email ? ` · ${r.creado_por_email}` : ""}` : ""}
                                        className={`h-16 rounded-md text-xs font-semibold flex flex-col items-center justify-center gap-0.5 px-1 ${cls} ${isCanceling ? "opacity-60 pointer-events-none" : ""}`}
                                    >
                                        <span className="font-mono text-sm leading-tight">{s.label}</span>
                                        {r && (
                                            <span className="text-[10px] font-medium leading-tight truncate max-w-full opacity-90">
                                                {uniName(r.unidad_id)}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Barra fija inferior */}
                    {count > 0 && (
                        <div
                            className="fixed bottom-0 left-0 right-0 md:left-64 z-40 border-t border-border bg-background/95 backdrop-blur-md shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.12)]"
                            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 56px)" }}
                            data-testid="cal-bar"
                        >
                            <div className="max-w-2xl mx-auto px-4 pt-3 pb-2">
                                <div className="flex items-baseline justify-between gap-3 mb-2">
                                    <p className="text-sm font-semibold leading-tight">
                                        {count} horario{count === 1 ? "" : "s"} seleccionado{count === 1 ? "" : "s"}
                                    </p>
                                    <p className="text-xs text-muted-foreground font-mono truncate">
                                        {unidadId ? uniName(unidadId) : "Sin unidad"}
                                    </p>
                                </div>
                                <Button
                                    onClick={reservar}
                                    disabled={submitting || !unidadId}
                                    className="w-full h-11"
                                    data-testid="cal-reservar"
                                >
                                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reservando…</> : "Reservar"}
                                </Button>
                            </div>
                        </div>
                    )}
                      </>
                    )}
                </TabsContent>

                {/* MÉTRICAS */}
                <TabsContent value="metricas">
                    <MetricasReservas />
                </TabsContent>
            </Tabs>
        </div>
    );
}
